import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, Loader2, CheckCircle2, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bottom-sheet cart for the guest menu page.
 *
 * `cart` shape:  [{ id, name, price, qty, variation_name? }, ...]
 * Persistence is owned by the parent (GuestMenuPage) via localStorage.
 */
const GuestCartSheet = ({ open, onClose, cart, sessionCode, onChange, onOrderPlaced }) => {
  const [tableNo, setTableNo] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null); // { order_id, status, eta_minutes, subtotal }

  const subtotal = cart.reduce((s, i) => s + (Number(i.price) || 0) * (i.qty || 1), 0);

  const setQty = (idx, q) => {
    const next = [...cart];
    if (q <= 0) next.splice(idx, 1);
    else next[idx] = { ...next[idx], qty: q };
    onChange(next);
  };

  const submit = async () => {
    if (!cart.length) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/petpooja/order`, {
        session_code: sessionCode,
        table_no: tableNo || null,
        guest_name: name || null,
        guest_phone: phone || null,
        items: cart.map((c) => ({
          id: String(c.id),
          name: c.name,
          price: Number(c.price) || 0,
          qty: c.qty || 1,
          variation_name: c.variation_name || null,
        })),
        note: note || null,
      });
      setPlaced(res.data);
      toast.success('Order placed', { description: `Est. ${res.data.eta_minutes || 15} min` });
      onChange([]); // clear cart
      onOrderPlaced?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not place order');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setPlaced(null);
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={close}
        className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
        data-testid="cart-sheet-backdrop"
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a] rounded-t-3xl border-t border-white/10 max-h-[92vh] flex flex-col"
        data-testid="cart-sheet"
      >
        <header className="flex items-center justify-between p-5 border-b border-white/5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-['JetBrains_Mono']">
              {placed ? 'Order Placed' : 'Your Order'}
            </p>
            <h3 className="text-2xl text-white font-['Outfit'] font-light mt-1" data-testid="cart-title">
              {placed ? `Confirmed #${placed.order_id.slice(0, 6).toUpperCase()}` : `${cart.length} item${cart.length === 1 ? '' : 's'}`}
            </h3>
          </div>
          <button onClick={close} className="text-gray-500 hover:text-white transition-colors" data-testid="cart-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {placed ? (
            <div className="text-center py-10">
              <CheckCircle2 className="w-14 h-14 text-[#39FF14] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-white text-lg font-['Outfit'] font-light">Sent to the kitchen</p>
              <p className="text-gray-400 text-sm mt-2 font-['IBM_Plex_Sans']">
                Est. {placed.eta_minutes || 15} minutes · Track from your table
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono']">Subtotal</span>
                <span className="text-white font-['JetBrains_Mono'] font-medium">{formatCurrency(placed.subtotal || subtotal)}</span>
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-500/60 font-['JetBrains_Mono'] mt-6">
                Demo mode — no real POS push yet
              </p>
            </div>
          ) : cart.length === 0 ? (
            <div className="text-center py-16">
              <UtensilsCrossed className="w-10 h-10 text-gray-700 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-gray-500 text-sm font-['IBM_Plex_Sans']">
                Your cart is empty. Tap “+ Order” on any menu item to add it.
              </p>
            </div>
          ) : (
            <>
              <ul className="space-y-3">
                {cart.map((c, idx) => (
                  <li key={`${c.id}-${idx}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5" data-testid={`cart-item-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-['IBM_Plex_Sans'] truncate">{c.name}</p>
                      {c.variation_name && (
                        <p className="text-gray-500 text-[11px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] mt-0.5">
                          {c.variation_name}
                        </p>
                      )}
                      <p className="text-[#D4AF37] text-xs font-['JetBrains_Mono'] mt-1">
                        {formatCurrency(c.price)} × {c.qty}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setQty(idx, (c.qty || 1) - 1)}
                        className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-colors"
                        data-testid={`cart-decrement-${c.id}`}
                      >
                        <Minus className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                      <span className="text-white text-sm font-['JetBrains_Mono'] w-5 text-center">{c.qty}</span>
                      <button
                        onClick={() => setQty(idx, (c.qty || 1) + 1)}
                        className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-colors"
                        data-testid={`cart-increment-${c.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => setQty(idx, 0)}
                        className="w-7 h-7 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-colors ml-1"
                        data-testid={`cart-remove-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-5 space-y-2.5">
                <input
                  value={tableNo}
                  onChange={(e) => setTableNo(e.target.value)}
                  placeholder="Table number (optional)"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-[#D4AF37]/50 focus:outline-none font-['IBM_Plex_Sans']"
                  data-testid="cart-table-input"
                />
                <div className="grid grid-cols-2 gap-2.5">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-[#D4AF37]/50 focus:outline-none font-['IBM_Plex_Sans']"
                    data-testid="cart-name-input"
                  />
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                    placeholder="Phone"
                    className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-[#D4AF37]/50 focus:outline-none font-['IBM_Plex_Sans']"
                    data-testid="cart-phone-input"
                  />
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Any special requests?"
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-[#D4AF37]/50 focus:outline-none font-['IBM_Plex_Sans'] resize-none"
                  data-testid="cart-note-input"
                />
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-500/60 font-['JetBrains_Mono'] mt-4 text-center">
                Demo mode · Petpooja push simulated
              </p>
            </>
          )}
        </div>

        {!placed && cart.length > 0 && (
          <footer className="p-5 border-t border-white/5 bg-black/40">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 text-xs uppercase tracking-[0.3em] font-['JetBrains_Mono']">Subtotal</span>
              <span className="text-white text-xl font-['JetBrains_Mono'] font-medium" data-testid="cart-subtotal">
                {formatCurrency(subtotal)}
              </span>
            </div>
            <button
              onClick={submit}
              disabled={submitting}
              className="w-full py-3.5 rounded-full bg-[#D4AF37] hover:bg-[#c19d2f] disabled:opacity-50 text-black font-['Outfit'] font-semibold tracking-wide transition-colors flex items-center justify-center gap-2"
              data-testid="cart-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Placing…</> : 'Place Order'}
            </button>
          </footer>
        )}
        {placed && (
          <footer className="p-5 border-t border-white/5">
            <button
              onClick={close}
              className="w-full py-3.5 rounded-full bg-white/10 hover:bg-white/15 text-white font-['Outfit'] font-medium transition-colors"
              data-testid="cart-done"
            >
              Done
            </button>
          </footer>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default GuestCartSheet;
